import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { StarRating } from "../../components/StarRating";
import { Trash2, EyeOff, CheckCircle2, MessageSquare, Star } from "lucide-react";
import { toast, Toaster } from "sonner";

export default function AdminReviews() {
    const [rows, setRows] = useState([]);
    const [status, setStatus] = useState("__all__");
    const [tgt, setTgt] = useState("__all__");
    const [busy, setBusy] = useState(false);

    const load = async () => {
        setBusy(true);
        try {
            const params = {};
            if (status !== "__all__") params.status = status;
            if (tgt !== "__all__") params.target_type = tgt;
            const { data } = await api.get("/admin/reviews", { params });
            setRows(data);
        } finally { setBusy(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, tgt]);

    const moderate = async (id, newStatus) => {
        try {
            await api.put(`/admin/reviews/${id}`, { status: newStatus });
            toast.success(newStatus === "published" ? "Published" : newStatus === "hidden" ? "Hidden" : "Reset to pending");
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };
    const del = async (id) => {
        if (!window.confirm("Delete this review permanently?")) return;
        try {
            await api.delete(`/admin/reviews/${id}`);
            toast.success("Deleted");
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    const counts = rows.reduce((acc, r) => { acc[r.status] = (acc[r.status] || 0) + 1; return acc; }, {});

    return (
        <div data-testid="admin-reviews-page" className="space-y-5">
            <Toaster position="top-center" />
            <div>
                <div className="label-eyebrow text-sky-700">Moderation</div>
                <h1 className="font-serif text-4xl text-stone-900">Reviews & ratings</h1>
                <p className="text-sm text-stone-500 mt-1">Auto-published reviews shown publicly. Hide spammy or off-topic posts; delete only for legal reasons.</p>
            </div>

            <div className="flex items-end gap-3 flex-wrap bg-white border border-stone-200 p-4">
                <div>
                    <label className="text-[10px] uppercase tracking-widest text-stone-500">Status</label>
                    <Select value={status} onValueChange={setStatus}>
                        <SelectTrigger className="rounded-none border-stone-300 mt-1 w-40" data-testid="reviews-status-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="__all__">All</SelectItem>
                            <SelectItem value="published">Published</SelectItem>
                            <SelectItem value="hidden">Hidden</SelectItem>
                            <SelectItem value="pending">Pending</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <label className="text-[10px] uppercase tracking-widest text-stone-500">Target</label>
                    <Select value={tgt} onValueChange={setTgt}>
                        <SelectTrigger className="rounded-none border-stone-300 mt-1 w-40" data-testid="reviews-target-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="__all__">All</SelectItem>
                            <SelectItem value="product">Products</SelectItem>
                            <SelectItem value="doctor">Doctors</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div className="ml-auto text-xs text-stone-500 flex gap-3">
                    <span data-testid="count-total">Total: {rows.length}</span>
                    <span className="text-emerald-700" data-testid="count-published">Published: {counts.published || 0}</span>
                    <span className="text-amber-700">Pending: {counts.pending || 0}</span>
                    <span className="text-red-700">Hidden: {counts.hidden || 0}</span>
                </div>
            </div>

            {rows.length === 0 ? (
                <div className="bg-white border border-stone-200 p-12 text-center text-sm text-stone-500">
                    <MessageSquare className="w-8 h-8 mx-auto mb-2 text-stone-300" />
                    {busy ? "Loading…" : "No reviews match these filters."}
                </div>
            ) : (
                <div className="bg-white border border-stone-200 overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead><tr className="bg-stone-50 border-b border-stone-200">
                            <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Date</th>
                            <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Target</th>
                            <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Reviewer</th>
                            <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Rating</th>
                            <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Comment</th>
                            <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Status</th>
                            <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Actions</th>
                        </tr></thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.id} className="border-b border-stone-100 hover:bg-sky-50/40 align-top" data-testid={`review-row-${r.id}`}>
                                    <td className="p-3 text-[11px] text-stone-500 font-mono">{(r.created_at || "").slice(0, 10)}</td>
                                    <td className="p-3">
                                        <div className="text-xs uppercase tracking-widest text-stone-500">{r.target_type}</div>
                                        <div className="font-medium text-stone-900 text-sm">{r.target_label}</div>
                                    </td>
                                    <td className="p-3">
                                        <div className="font-medium text-stone-900 text-sm">{r.user_name}</div>
                                        <div className="text-[10px] text-stone-500 font-mono">{r.user_email}</div>
                                    </td>
                                    <td className="p-3"><StarRating value={r.rating} size={13} showValue /></td>
                                    <td className="p-3 max-w-md">
                                        {r.title && <div className="text-xs font-semibold text-stone-900 mb-1">{r.title}</div>}
                                        <div className="text-xs text-stone-700 whitespace-pre-line line-clamp-4">{r.comment}</div>
                                    </td>
                                    <td className="p-3">
                                        <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 ${r.status === "published" ? "bg-emerald-100 text-emerald-800" : r.status === "hidden" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}`}>{r.status}</span>
                                    </td>
                                    <td className="p-3 text-right">
                                        <div className="inline-flex gap-1 flex-wrap justify-end">
                                            {r.status !== "published" && (
                                                <Button size="sm" variant="outline" className="rounded-none h-7 px-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={() => moderate(r.id, "published")} data-testid={`publish-${r.id}`}>
                                                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Publish
                                                </Button>
                                            )}
                                            {r.status !== "hidden" && (
                                                <Button size="sm" variant="outline" className="rounded-none h-7 px-2 border-amber-300 text-amber-700 hover:bg-amber-50" onClick={() => moderate(r.id, "hidden")} data-testid={`hide-${r.id}`}>
                                                    <EyeOff className="w-3.5 h-3.5 mr-1" />Hide
                                                </Button>
                                            )}
                                            <Button size="sm" variant="outline" className="rounded-none h-7 px-2 border-red-300 text-red-700 hover:bg-red-50" onClick={() => del(r.id)} data-testid={`delete-${r.id}`}>
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
